import React, { useState, useEffect } from "react";
import { Typography, Col, Row, notification, Spin, Space } from "antd";
import { useNavigate, Link } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { PrimaryButton } from "../../shared/button/Buttons";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import DatasetsTable from "./DatasetsTable";

const Datasets: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
  };

  // Add minimal CSS
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .datasets-container {
        background-color: #f5f7fa;
      }
      .interactive-row {
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchDatasets();
  }, [currentPage, apiKey]);

  const fetchDatasets = () => {
    const offset = (currentPage - 1) * pageSize;
    DatasetService.getDatasets(offset, pageSize, setLoading, apiKey).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch datasets: ${error}`,
        });
      }
    );
  };

  if (loading && datasets.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
        minHeight: "calc(100vh - 64px)",
      }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/">Home</Link> / Finetuning / Datasets
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                Datasets
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage datasets for model fine-tuning
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Datasets
          </Typography.Title>
          <PrimaryButton
            title="New Dataset"
            icon={<PlusOutlined />}
            onClick={() => navigate("/models/datasets/new")}
          />
        </div>

        <DatasetsTable
          datasets={datasets}
          loading={loading}
          total={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onRefreshData={fetchDatasets}
          setLoading={setLoading}
        />
      </div>
    </div>
  );
};

export default Datasets;
